/**
 * @file usePreviewTargets.ts
 * @description Товары и страницы для экранов предпросмотра «Товар» и «Любая
 * страница…» (phase19.16 Р1; uiux-design-r1 §1: «первый товар в наличии,
 * плюс «Другой товар…»).
 *
 * Списки грузятся только при первом выборе такого экрана и только при праве
 * на раздел (каталог — право каталога, страницы — право контента): без
 * права экран показывает каталог или главную, а не ошибку.
 */

import { useCallback, useState } from "react";

import { useHasPermission } from "../../auth/hooks";
import { catalogProducts } from "../../lib/api-catalog";
import { sitePages } from "../../lib/api-site-pages";

export interface PreviewTarget {
  slug: string;
  label: string;
}

export interface PreviewTargets {
  products: PreviewTarget[] | null;
  pages: PreviewTarget[] | null;
  /** Загрузить список для экрана (повторный вызов не грузит заново). */
  ensure: (kind: "product" | "page") => void;
}

export function usePreviewTargets(): PreviewTargets {
  const canCatalog = useHasPermission("SHOP_MANAGE_CATALOG");
  const canContent = useHasPermission("SHOP_MANAGE_CONTENT");
  const [products, setProducts] = useState<PreviewTarget[] | null>(null);
  const [pages, setPages] = useState<PreviewTarget[] | null>(null);

  const ensure = useCallback(
    (kind: "product" | "page") => {
      if (kind === "product" && products === null) {
        if (!canCatalog) return setProducts([]);
        void catalogProducts
          .list({ withStock: false })
          .then((list) =>
            setProducts(
              list
                .filter((p) => p.status === "ACTIVE")
                .map((p) => ({ slug: p.slug, label: p.displayName })),
            ),
          )
          .catch(() => setProducts([]));
      }
      if (kind === "page" && pages === null) {
        if (!canContent) return setPages([]);
        void sitePages
          .list()
          .then((list) =>
            setPages(list.filter((p) => p.isPublished).map((p) => ({ slug: p.slug, label: p.title }))),
          )
          .catch(() => setPages([]));
      }
    },
    [canCatalog, canContent, pages, products],
  );

  return { products, pages, ensure };
}
